import { useTranslations } from 'next-intl';

/**
 * Translates a key using the primary translator, and if the key is
 * not found, it falls back to the provided fallback translators in order.
 *
 * @param key - The translation key to look up.
 * @param primaryTranslator - The primary translator to use for translation.
 * @param fallbackTranslators - An array of fallback translators to use if
 *                              the key is not found in the primary translator.
 *
 * @returns The translated string if found, otherwise 'Error'.
 *
 * @example
 * const apiErrorTranslator = useTranslations('api-errors');
 * const commonErrorTranslator = useTranslations('common-errors');
 *
 * const errorMessage = translateWithFallback(
 *   'network_error',
 *   apiErrorTranslator,
 *   commonErrorTranslator
 * );
 * // If 'network_error' is found in 'api-errors', it will be returned.
 * // If not, it will check 'common-errors' and return the translation if found.
 * // If the key is not found in either translator, it will return 'Error'.
 */
export function translateWithFallback(
  key: string,
  primaryTranslator: ReturnType<typeof useTranslations>,
  ...fallbackTranslators: ReturnType<typeof useTranslations>[]
) {
  if (primaryTranslator.has(key)) {
    return primaryTranslator(key);
  }

  for (const translator of fallbackTranslators) {
    if (!translator.has(key)) {
      continue;
    }

    return translator(key);
  }

  return 'Error';
}
